import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  LinearProgress,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  AccessTimeOutlined as TimerIcon,
  StorefrontOutlined as StoreIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import StatCard from '../../Components/GatewayContagem/StatCard';
import PageHeader from '../../Components/GatewayContagem/PageHeader';
import EmptyTableRow from '../../Components/GatewayContagem/EmptyTableRow';
import DateRangePopover from '../../Components/GatewayContagem/DateRangePopover';
import LoadingSkeleton from '../../Components/GatewayContagem/LoadingSkeleton';
import useGatewayContagem from '../../Context/Hooks/useGatewayContagem';
import { countingAPI } from '../../API/countingAPI';
import { deviceAPI } from '../../API/deviceAPI';
import { heatmapAPI } from '../../API/heatmapAPI';
import { fmtDate, fmtDwell, TABLE_HEAD_ROW_SX } from '../../Utils/format';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TOGGLE_SX = {
  '& .MuiToggleButton-root': {
    px: 1.5,
    py: 0.5,
    fontSize: '0.8125rem',
    fontWeight: 500,
    textTransform: 'none',
    border: '1px solid',
    borderColor: 'divider',
    color: 'text.secondary',
    '&.Mui-selected': {
      bgcolor: 'primary.dark',
      color: '#fff',
      borderColor: 'primary.dark',
      '&:hover': {
        bgcolor: 'primary.main',
      },
    },
  },
};

const periodToRange = (p) => {
  const now = new Date();
  const start = new Date();
  if (p === 'today') {
    start.setHours(0, 0, 0, 0);
  } else if (p === '7d') {
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);
  } else if (p === '30d') {
    start.setDate(start.getDate() - 29);
    start.setHours(0, 0, 0, 0);
  } else if (p === 'custom') {
    start.setDate(start.getDate() - 1);
    start.setHours(0, 0, 0, 0);
    now.setHours(23, 59, 59, 999);
  }
  return { start, end: now };
};

const toDateKey = (d) => {
  if (!d) {
    return '';
  }
  if (typeof d === 'string') {
    return d.substring(0, 10);
  }
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) {
    return '';
  }
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const DwellTimePage = () => {
  const { t, i18n } = useTranslation('dwell');
  const { selectedClient, stores: clientStores, loadStores } = useGatewayContagem();

  // Filters state
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [cachedDevices, setCachedDevices] = useState([]);
  const [availableZones, setAvailableZones] = useState([]);
  const [selectedZoneIds, setSelectedZoneIds] = useState([]);
  const [period, setPeriod] = useState('today');
  const [startDate, setStartDate] = useState(() => periodToRange('today').start);
  const [endDate, setEndDate] = useState(() => periodToRange('today').end);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Data & Loading state
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [isClientPaginated, setIsClientPaginated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  // Ensure clientStores is loaded
  useEffect(() => {
    if (selectedClient?.id && (!clientStores || clientStores.length === 0)) {
      loadStores(selectedClient.id);
    }
  }, [selectedClient?.id, clientStores, loadStores]);

  // Reset store and zone selection when client changes
  useEffect(() => {
    setSelectedStoreIds([]);
    setAvailableZones([]);
    setSelectedZoneIds([]);
    setPage(0);
  }, [selectedClient?.id]);

  const handlePeriodChange = (_, newPeriod) => {
    if (!newPeriod) {
      return;
    }
    setIsLoading(true);
    setKpiLoading(true);
    setPeriod(newPeriod);
    const { start, end } = periodToRange(newPeriod);
    setStartDate(start);
    setEndDate(end);
    setPage(0);
  };

  const handleStartChange = (d) => {
    setIsLoading(true);
    setKpiLoading(true);
    setStartDate(d);
    setPeriod(null);
    setPage(0);
  };

  const handleEndChange = (d) => {
    setIsLoading(true);
    setKpiLoading(true);
    setEndDate(d);
    setPeriod(null);
    setPage(0);
  };

  // Cache devices when stores change (reused by both zone useEffect and refresh)
  useEffect(() => {
    if (selectedStoreIds.length === 0) {
      setCachedDevices([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const devicePromises = selectedStoreIds.map(async sId => {
          const [byStoreRes, historyRes] = await Promise.allSettled([
            deviceAPI.getByStore(sId),
            deviceAPI.getStoreHistory(sId),
          ]);
          const activeList = (byStoreRes.status === 'fulfilled' && Array.isArray(byStoreRes.value?.value))
            ? byStoreRes.value.value
            : [];
          const historyList = (historyRes.status === 'fulfilled' && Array.isArray(historyRes.value?.value))
            ? historyRes.value.value
            : [];

          const devMap = new Map();
          // 1. Process history records first (authoritative timestamps and stint records)
          historyList.forEach(h => {
            const stint = {
              associatedAt: h.associatedAt,
              disassociatedAt: h.disassociatedAt,
              isCurrent: h.isCurrent,
            };
            if (!devMap.has(h.deviceId)) {
              devMap.set(h.deviceId, {
                id: h.deviceId,
                name: h.deviceName,
                serialNumber: h.serialNumber,
                storeId: sId,
                isCurrent: h.isCurrent,
                stints: [stint],
              });
            } else {
              const existing = devMap.get(h.deviceId);
              if (h.isCurrent) {
                existing.isCurrent = true;
              }
              if (!existing.stints) {
                existing.stints = [];
              }
              existing.stints.push(stint);
            }
          });

          // 2. Add any active device without history records yet (defensive fallback for legacy cameras)
          activeList.forEach(d => {
            if (!devMap.has(d.id)) {
              devMap.set(d.id, {
                ...d,
                storeId: sId,
                isCurrent: true,
                stints: [{ associatedAt: null, disassociatedAt: null, isCurrent: true }],
              });
            } else {
              const existing = devMap.get(d.id);
              existing.isCurrent = true;
              if (!existing.name && d.name) {
                existing.name = d.name;
              }
            }
          });
          return Array.from(devMap.values());
        });
        const deviceResults = await Promise.all(devicePromises);
        const devices = deviceResults.flat().sort((a, b) => {
          const aCurrent = a.isCurrent !== false;
          const bCurrent = b.isCurrent !== false;
          if (aCurrent && !bCurrent) {
            return -1;
          }
          if (!aCurrent && bCurrent) {
            return 1;
          }
          return (a.name || a.serialNumber || '').localeCompare(b.name || b.serialNumber || '');
        });
        if (!cancelled) {
          setCachedDevices(devices);
        }
      } catch (err) {
        console.error('[DwellTimePage] Error loading devices:', err);
        if (!cancelled) {
          setCachedDevices([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedStoreIds]);

  // Filter devices that were associated with the selected store(s) during [startDate, endDate]
  const relevantDevices = useMemo(() => {
    if (cachedDevices.length === 0) {
      return [];
    }
    const filterStart = startDate ? new Date(startDate).getTime() : 0;
    const filterEnd = endDate ? new Date(endDate).getTime() : Infinity;

    return cachedDevices.filter(dev => {
      if (!dev.stints || dev.stints.length === 0) {
        return dev.isCurrent !== false;
      }
      return dev.stints.some(stint => {
        if (stint.isCurrent) {
          if (stint.associatedAt && new Date(stint.associatedAt).getTime() > filterEnd) {
            return false;
          }
          return true;
        }
        const stintStart = stint.associatedAt ? new Date(stint.associatedAt).getTime() : 0;
        const stintEnd = stint.disassociatedAt ? new Date(stint.disassociatedAt).getTime() : Infinity;
        return stintStart <= filterEnd && stintEnd >= filterStart;
      });
    });
  }, [cachedDevices, startDate, endDate]);

  // Load zones for all selected stores whenever relevantDevices updates
  useEffect(() => {
    if (relevantDevices.length === 0) {
      setAvailableZones([]);
      setSelectedZoneIds([]);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const zonePromises = relevantDevices.map(d => deviceAPI.getZones(d.id));
        const zoneResults = await Promise.all(zonePromises);

        const dwellZones = [];
        zoneResults.forEach((res, idx) => {
          const list = res?.value ?? res ?? [];
          const dev = relevantDevices[idx];
          const store = (clientStores || []).find(s => s.id === dev?.storeId);
          const storeName = store?.name || dev?.name || 'Loja';
          if (Array.isArray(list) && dev) {
            list.forEach(z => {
              const type = (z.xovisLogicType || z.XovisLogicType || z.type || z.Type || '').toUpperCase().trim();
              const name = (z.name || z.Name || z.zoneName || '').toLowerCase().trim();

              // Strictly exclude geometries and non-dwell counting lines
              const isGeometry = type.includes('GEOMETRY') || name.includes('geometry') || name.includes('geometria') || type === 'GENERAL' || name === 'general' || name === 'geral';
              const isCountingLine = type.includes('LINE') || type.includes('IN_OUT');
              const isTrueDwellLogic = !isGeometry && !isCountingLine && (type.includes('ATTENTION') || type.includes('DWELL') || type.includes('OCCUPANCY'));

              if (isTrueDwellLogic) {
                const zoneId = z.id || z.Id;
                const logicId = z.xovisLogicId || z.XovisLogicId;
                const selectKey = `${dev.id}__${dev.storeId}__${logicId ?? zoneId}`;
                dwellZones.push({
                  id: zoneId,
                  selectKey,
                  name: z.name || z.Name || t('table.logicNumber', 'Lógica #{{id}}', { id: logicId }),
                  logicId,
                  deviceId: dev.id || dev.Id,
                  deviceName: dev.name || dev.Name,
                  storeId: dev.storeId,
                  storeName,
                  isHistorical: !dev.isCurrent,
                  type,
                });
              }
            });
          }
        });

        dwellZones.sort((a, b) => {
          if (!a.isHistorical && b.isHistorical) {
            return -1;
          }
          if (a.isHistorical && !b.isHistorical) {
            return 1;
          }
          const storeComp = (a.storeName || '').localeCompare(b.storeName || '');
          if (storeComp !== 0) {
            return storeComp;
          }
          const devComp = (a.deviceName || '').localeCompare(b.deviceName || '');
          if (devComp !== 0) {
            return devComp;
          }
          return (a.name || '').localeCompare(b.name || '');
        });

        if (!cancelled) {
          setAvailableZones(dwellZones);
        }
      } catch (err) {
        console.error('[DwellTimePage] Error loading store zones:', err);
        if (!cancelled) {
          setAvailableZones([]);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [relevantDevices, clientStores, t]);

  // Sanitize selectedZoneIds if availableZones changes
  useEffect(() => {
    if (selectedZoneIds.length > 0) {
      if (availableZones.length === 0) {
        setSelectedZoneIds([]);
      } else {
        const validIds = new Set(availableZones.map(z => z.selectKey || String(z.id || z.logicId)));
        setSelectedZoneIds(prev => {
          const filtered = prev.filter(id => validIds.has(String(id)));
          return filtered.length === prev.length ? prev : filtered;
        });
      }
    }
  }, [availableZones]);

  // Track active request to prevent race conditions
  const activeRequestIdRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!selectedClient?.id) {
      return;
    }
    const requestId = ++activeRequestIdRef.current;
    setIsLoading(true);
    try {
      if (selectedStoreIds.length === 0) {
        // Mode A: All Stores View -> Server-side paginated request
        const res = await countingAPI.getDwellSummary(
          selectedClient.id,
          startDate,
          endDate,
          null,
          page + 1,
          rowsPerPage
        );

        if (requestId !== activeRequestIdRef.current) {
          return;
        }

        const mapItems = (items) => items.map(r => ({
          storeId: r.storeId || r.StoreId,
          storeName: r.storeName || r.StoreName,
          deviceName: r.deviceName || r.DeviceName || null,
          zoneName: null,
          logicId: null,
          date: toDateKey(r.date || r.Date),
          dwellSeconds: (r.avgDwellSeconds ?? r.AvgDwellSeconds) ?? 0,
          maxDwellSeconds: (r.maxDwellSeconds ?? r.MaxDwellSeconds) != null ? (r.maxDwellSeconds ?? r.MaxDwellSeconds) : null,
          minDwellSeconds: (r.minDwellSeconds ?? r.MinDwellSeconds) != null ? (r.minDwellSeconds ?? r.MinDwellSeconds) : null,
          visitorCount: (r.visitorCount ?? r.VisitorCount) ?? 0,
        }));

        const paginatedObj = res?.value ?? res ?? {};
        const rawItems = Array.isArray(paginatedObj)
          ? paginatedObj
          : (paginatedObj.items ?? paginatedObj.Items ?? []);
        const count = paginatedObj.totalCount ?? paginatedObj.TotalCount ?? rawItems.length;

        const mapped = mapItems(rawItems);
        setRows(mapped);
        setAllRows(mapped);
        setTotalCount(count);
        setIsClientPaginated(false);
      } else {
        // Mode B: Specific Store(s) Selected -> Detailed Per-Zone Logic Breakdown
        const mapItems = (items) => items.map(r => ({
          storeId: r.storeId || r.StoreId,
          storeName: r.storeName || r.StoreName,
          deviceName: r.deviceName || r.DeviceName || null,
          zoneName: null,
          logicId: null,
          date: toDateKey(r.date || r.Date),
          dwellSeconds: (r.avgDwellSeconds ?? r.AvgDwellSeconds) ?? 0,
          maxDwellSeconds: (r.maxDwellSeconds ?? r.MaxDwellSeconds) != null ? (r.maxDwellSeconds ?? r.MaxDwellSeconds) : null,
          minDwellSeconds: (r.minDwellSeconds ?? r.MinDwellSeconds) != null ? (r.minDwellSeconds ?? r.MinDwellSeconds) : null,
          visitorCount: (r.visitorCount ?? r.VisitorCount) ?? 0,
        }));

        const fallbackToStoreSummary = async () => {
          const res = await countingAPI.getDwellSummary(
            selectedClient.id,
            startDate,
            endDate,
            selectedStoreIds,
            page + 1,
            rowsPerPage
          );
          if (requestId !== activeRequestIdRef.current) {
            return;
          }
          const paginatedObj = res?.value ?? res ?? {};
          const rawItems = Array.isArray(paginatedObj)
            ? paginatedObj
            : (paginatedObj.items ?? paginatedObj.Items ?? []);
          const count = paginatedObj.totalCount ?? paginatedObj.TotalCount ?? rawItems.length;
          const mapped = mapItems(rawItems);
          setRows(mapped);
          setAllRows(mapped);
          setTotalCount(count);
          setIsClientPaginated(false);
        };

        if (relevantDevices.length === 0) {
          if (selectedZoneIds.length > 0) {
            setRows([]);
            setAllRows([]);
            setTotalCount(0);
            return;
          }
          await fallbackToStoreSummary();
          return;
        }

        const summaryResults = await Promise.all(
          relevantDevices.map(d => heatmapAPI.getTemporalSummary(d.id, startDate, endDate, d.storeId))
        );

        if (requestId !== activeRequestIdRef.current) {
          return;
        }

        const startKey = toDateKey(startDate);
        const endKey = toDateKey(endDate);
        const startFormatted = fmtDate(startKey);
        const endFormatted = fmtDate(endKey);
        const periodDateLabel = startFormatted === endFormatted ? startFormatted : `${startFormatted} – ${endFormatted}`;

        const deviceStoreZonesMap = new Map();
        availableZones.forEach(z => {
          const key = `${z.deviceId}__${z.storeId}`;
          if (!deviceStoreZonesMap.has(key)) {
            deviceStoreZonesMap.set(key, []);
          }
          deviceStoreZonesMap.get(key).push(z);
        });

        const zoneRows = [];
        summaryResults.forEach((sRes, idx) => {
          const summary = sRes?.value || sRes;
          const device = relevantDevices[idx];
          const store = (clientStores || []).find(s => s.id === device.storeId);
          const storeName = store?.name || device.name || 'Loja';
          const deviceName = device.name || device.Name || null;

          const logicCountersMap = new Map();
          if (Array.isArray(summary?.logicCounters)) {
            summary.logicCounters.forEach(lc => {
              if (lc.zoneId) {
                logicCountersMap.set(String(lc.zoneId).toLowerCase(), lc);
              }
              if (lc.xovisLogicId !== null && lc.xovisLogicId !== undefined) {
                logicCountersMap.set(String(lc.xovisLogicId), lc);
              }
              if (lc.zoneName) {
                logicCountersMap.set(lc.zoneName.toLowerCase().trim(), lc);
              }
            });
          }

          const deviceKey = `${device.id}__${device.storeId}`;
          const zonesForDevice = deviceStoreZonesMap.get(deviceKey) || [];
          zonesForDevice.forEach(z => {
            const lc = (z.id && logicCountersMap.get(String(z.id).toLowerCase()))
              || (z.logicId !== null && z.logicId !== undefined && logicCountersMap.get(String(z.logicId)))
              || (z.name && logicCountersMap.get(z.name.toLowerCase().trim()))
              || {};

            const avgDwell = lc.avgDwellTimeSeconds ?? lc.avgDwellSeconds ?? 0;
            const visitorCount = lc.cumulativeVisits ?? lc.totalIn ?? lc.count ?? 0;
            const logicId = z.logicId ?? z.id;
            const selectKey = z.selectKey || `${z.deviceId}__${z.storeId}__${logicId}`;

            const isExplicitlySelected = selectedZoneIds.length > 0 && selectedZoneIds.some(sel => {
              if (sel.includes('__')) {
                return sel === selectKey;
              }
              return String(logicId) === String(sel) || String(z.id) === String(sel) || z.name === sel;
            });

            if (selectedZoneIds.length > 0 && !isExplicitlySelected) {
              return;
            }

            if (avgDwell > 0 || visitorCount > 0 || isExplicitlySelected) {
              zoneRows.push({
                storeId: device.storeId,
                storeName,
                deviceName,
                zoneName: z.name,
                logicId,
                date: periodDateLabel,
                dwellSeconds: avgDwell,
                maxDwellSeconds: lc.maxDwellTimeSeconds ?? null,
                minDwellSeconds: lc.minDwellTimeSeconds ?? null,
                visitorCount,
              });
            }
          });
        });

        if (zoneRows.length === 0) {
          if (selectedZoneIds.length > 0) {
            setRows([]);
            setAllRows([]);
            setTotalCount(0);
            setIsClientPaginated(false);
            return;
          }
          await fallbackToStoreSummary();
          return;
        }

        setRows(zoneRows);
        setAllRows(zoneRows);
        setTotalCount(zoneRows.length);
        setIsClientPaginated(true);
      }
    } catch (err) {
      console.error('[DwellTimePage] Error loading dwell data:', err);
      setRows([]);
      setAllRows([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
      setHasFetched(true);
    }
  }, [selectedClient?.id, startDate, endDate, selectedStoreIds, selectedZoneIds, clientStores, relevantDevices, availableZones, page, rowsPerPage]);

  useEffect(() => { refresh(); }, [refresh]);

  const stores = useMemo(() => {
    if (clientStores?.length > 0) {
      return clientStores.map(s => ({ id: s.id, name: s.name })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    return [...new Map(allRows.map(r => [r.storeId, { id: r.storeId, name: r.storeName }])).values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [clientStores, allRows]);

  const maxDwell = useMemo(
    () => Math.max(...rows.map(r => r.dwellSeconds), 1),
    [rows]
  );

  // Sliced rows for table rendering when client-side pagination is in effect (Mode B zone rows)
  const displayedRows = useMemo(() => {
    if (!isClientPaginated) {
      return rows;
    }
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [isClientPaginated, rows, page, rowsPerPage]);

  // KPI data from dedicated backend endpoint (no large client-side fetch needed)
  const [kpiData, setKpiData] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(true);

  useEffect(() => {
    if (!selectedClient?.id) {
      setKpiData(null);
      setKpiLoading(false);
      return;
    }
    let cancelled = false;
    setKpiLoading(true);
    (async () => {
      try {
        const res = await countingAPI.getDwellKpi(
          selectedClient.id,
          startDate,
          endDate,
          selectedStoreIds.length > 0 ? selectedStoreIds : null
        );
        const val = res?.value ?? res;
        if (!cancelled && val) {
          setKpiData(val);
        }
      } catch (err) {
        console.error('[DwellTimePage] Error loading KPI data:', err);
      } finally {
        if (!cancelled) {
          setKpiLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedClient?.id, startDate, endDate, selectedStoreIds]);

  const kpi = useMemo(() => {
    const kpiAvg = kpiData ? (kpiData.globalAvgDwellSeconds ?? kpiData.GlobalAvgDwellSeconds ?? 0) : 0;
    if (kpiData && (kpiAvg > 0 || (kpiData.peakDwellSeconds ?? kpiData.PeakDwellSeconds ?? 0) > 0)) {
      return {
        avg: kpiAvg,
        peak: (kpiData.peakDate || kpiData.PeakDate) ? {
          date: kpiData.peakDate || kpiData.PeakDate,
          dwellSeconds: kpiData.peakDwellSeconds ?? kpiData.PeakDwellSeconds ?? 0,
          storeName: kpiData.peakStoreName || kpiData.PeakStoreName || '',
        } : null,
        topStore: (kpiData.topStoreName || kpiData.TopStoreName) ? {
          name: kpiData.topStoreName || kpiData.TopStoreName,
          totalSec: kpiData.topStoreAvgDwellSeconds ?? kpiData.TopStoreAvgDwellSeconds ?? 0,
          totalVis: 1,
        } : null,
      };
    }
    // Fallback: compute from visible rows
    const src = rows;
    if (!src.length) return { avg: 0, peak: null, topStore: null };
    const withVis = src.filter(r => (r.visitorCount || 0) > 0);
    const totalVis = withVis.reduce((s, r) => s + r.visitorCount, 0);
    const avg = totalVis > 0
      ? Math.round(withVis.reduce((s, r) => s + r.dwellSeconds * r.visitorCount, 0) / totalVis)
      : Math.round(src.reduce((s, r) => s + r.dwellSeconds, 0) / src.length);
    const peak = src.reduce(
      (m, r) => (r.dwellSeconds > (m?.dwellSeconds ?? 0) ? r : m),
      null
    );
    const byStore = {};
    src.forEach(r => {
      if (!byStore[r.storeId]) byStore[r.storeId] = { name: r.storeName, totalSec: 0, totalVis: 0 };
      byStore[r.storeId].totalSec += r.dwellSeconds * (r.visitorCount || 1);
      byStore[r.storeId].totalVis += (r.visitorCount || 1);
    });
    const topStore = Object.values(byStore)
      .sort((a, b) => (b.totalSec / b.totalVis) - (a.totalSec / a.totalVis))[0];
    return { avg, peak, topStore };
  }, [kpiData, rows]);

  const noClient = !selectedClient?.id;
  const showZoneColumn = selectedStoreIds.length > 0 || selectedZoneIds.length > 0;
  const isModeB = selectedStoreIds.length > 0;

  return (
    <Box sx={{ p: 3, position: 'relative' }}>
      {/* ── Header matching DashboardPage layout ── */}
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        storeLabel={selectedStoreIds.length === 1 ? (stores.find(s => s.id === selectedStoreIds[0])?.name ?? null) : null}
      >
        {/* Multiselect Store Dropdown */}
        <Select
          size="small"
          multiple
          value={selectedStoreIds}
          onChange={e => {
            const val = e.target.value;
            setIsLoading(true);
            setKpiLoading(true);
            setSelectedStoreIds(typeof val === 'string' ? val.split(',') : val);
            setSelectedZoneIds([]);
            setPage(0);
          }}
          displayEmpty
          renderValue={selected => (
            selected.length === 0
              ? <span style={{ opacity: 0.55 }}>{t('filters.allStores')}</span>
              : <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map(id => {
                    const store = stores.find(s => s.id === id);
                    return <Chip key={id} label={store?.name || id} size="small" />;
                  })}
                </Box>
          )}
          sx={{ fontSize: '0.8125rem', minWidth: 160, maxWidth: 280 }}
        >
          {stores.map(s => (
            <MenuItem key={s.id} value={s.id} sx={{ fontSize: '0.8125rem' }}>
              <Checkbox checked={selectedStoreIds.includes(s.id)} size="small" />
              <ListItemText primary={s.name} />
            </MenuItem>
          ))}
        </Select>

        {/* Multiselect Zone Dropdown — enabled when stores are selected */}
        <Select
          size="small"
          multiple
          disabled={selectedStoreIds.length === 0}
          value={selectedZoneIds}
          onChange={e => {
            const val = e.target.value;
            setSelectedZoneIds(typeof val === 'string' ? val.split(',') : val);
            setPage(0);
          }}
          displayEmpty
          renderValue={selected => (
            selected.length === 0
              ? <span style={{ opacity: 0.55 }}>{t('filters.allZones', 'Todas as Zonas')}</span>
              : <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map(val => {
                    const z = availableZones.find(item => item.selectKey === val || String(item.id) === String(val) || String(item.logicId) === String(val));
                    const hasDuplicateName = availableZones.filter(item => item.name === z?.name).length > 1;
                    let chipLabel = z?.name || val;
                    if (z) {
                      if (z.isHistorical) {
                        chipLabel = `${z.name} (${z.deviceName} • ${z.storeName})`;
                      } else if (hasDuplicateName || selectedStoreIds.length > 1) {
                        chipLabel = `${z.name} (${z.deviceName} • ${z.storeName})`;
                      } else {
                        chipLabel = `${z.name} (${z.deviceName})`;
                      }
                    }
                    return <Chip key={val} label={chipLabel} size="small" />;
                  })}
                </Box>
          )}
          sx={{ fontSize: '0.8125rem', minWidth: 160, maxWidth: 280 }}
        >
          {availableZones.map((z, index) => {
            const val = z.selectKey || String(z.id || z.logicId);
            const secondaryText = z.isHistorical
              ? `${z.deviceName} • ${z.storeName} (${t('filters.historical', 'Histórico')})`
              : (selectedStoreIds.length > 1 ? `${z.deviceName} • ${z.storeName}` : z.deviceName);
            const showDivider = index > 0 && !availableZones[index - 1].isHistorical && z.isHistorical;
            return [
              showDivider && <Divider key={`divider-${val}`} sx={{ my: 0.5 }} />,
              <MenuItem key={val} value={val} sx={{ fontSize: '0.8125rem' }}>
                <Checkbox checked={selectedZoneIds.includes(val)} size="small" />
                <ListItemText
                  primary={z.name}
                  secondary={secondaryText}
                  primaryTypographyProps={{ fontSize: '0.8125rem' }}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                />
              </MenuItem>
            ];
          })}
        </Select>

        {/* PeriodToggle: today, 7d, 30d */}
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={handlePeriodChange}
          size="small"
          sx={TOGGLE_SX}
        >
          <ToggleButton value="today">{t('period.today')}</ToggleButton>
          <ToggleButton value="7d">7d</ToggleButton>
          <ToggleButton value="30d">30d</ToggleButton>
          <ToggleButton value="custom">{t('period.custom', 'Personalizado')}</ToggleButton>
        </ToggleButtonGroup>

        {/* Date Range Popover */}
        <DateRangePopover
          startDate={startDate}
          endDate={endDate}
          onStartChange={handleStartChange}
          onEndChange={handleEndChange}
        />
      </PageHeader>

      {/* ── KPI cards ── */}
      {(() => {
        const isDataLoading = !selectedClient?.id || (selectedStoreIds.length === 0 ? kpiLoading : isLoading);
        return (
          <Stack
            direction="row"
            alignItems="stretch"
            gap={2}
            mb={3}
            flexWrap="wrap"
          >
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <StatCard
                icon={<TimerIcon fontSize="inherit" />}
                title={t('kpi.globalAvg')}
                value={fmtDwell(kpi.avg)}
                loading={isDataLoading}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <StatCard
                icon={<TrendingUpIcon fontSize="inherit" />}
                title={t('kpi.peakDay')}
                value={kpi.peak ? fmtDwell(kpi.peak.dwellSeconds) : '—'}
                subtitle={kpi.peak ? `${typeof kpi.peak.date === 'string' && kpi.peak.date.includes('/') ? kpi.peak.date : fmtDate(toDateKey(kpi.peak.date))} · ${kpi.peak.storeName}` : ''}
                loading={isDataLoading}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <StatCard
                icon={<StoreIcon fontSize="inherit" />}
                title={t('kpi.topStore')}
                value={kpi.topStore && kpi.topStore.totalVis > 0 ? fmtDwell(Math.round(kpi.topStore.totalSec / kpi.topStore.totalVis)) : '—'}
                subtitle={kpi.topStore?.name ?? ''}
                loading={isDataLoading}
              />
            </Box>
          </Stack>
        );
      })()}

      {/* ── Table Container ── */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '14px',
          overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
          position: 'relative',
        }}
      >
        {noClient ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('table.selectClient')}
            </Typography>
          </Box>
        ) : (
          <>
            {selectedZoneIds.length > 0 && (
              <Box sx={{ px: 2.5, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">{t('filters.activeZoneFilters')}</Typography>
                {selectedZoneIds.map(val => {
                  const z = availableZones.find(item => item.selectKey === val || String(item.id) === String(val) || String(item.logicId) === String(val));
                  const hasDuplicateName = availableZones.filter(item => item.name === z?.name).length > 1;
                  let chipLabel = z?.name || val;
                  if (z) {
                    if (z.isHistorical) {
                      chipLabel = `${z.name} (${z.deviceName} • ${z.storeName})`;
                    } else if (hasDuplicateName || selectedStoreIds.length > 1) {
                      chipLabel = `${z.name} (${z.deviceName} • ${z.storeName})`;
                    } else {
                      chipLabel = `${z.name} (${z.deviceName})`;
                    }
                  }
                  return <Chip key={val} label={chipLabel} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />;
                })}
              </Box>
            )}

            <LoadingSkeleton loading={isLoading} preset="table" height={360} sx={{ borderRadius: 0 }}>
              <TableContainer>
                <Table
                  size="small"
                  stickyHeader
                  sx={{ '& .MuiTableCell-root': { fontSize: 13, borderColor: 'divider' } }}
                >
                  <TableHead>
                    <TableRow sx={TABLE_HEAD_ROW_SX}>
                      <TableCell sx={{ pl: 2.5 }}>{t('table.date')}</TableCell>
                      <TableCell>{t('table.store')}</TableCell>
                      {isModeB && <TableCell>{t('table.device', 'Dispositivo')}</TableCell>}
                      {showZoneColumn && <TableCell>{t('table.zone', 'Zona / Lógica')}</TableCell>}
                      <TableCell align="right">{t('table.visitors')}</TableCell>
                      <TableCell>{t('table.dwell')}</TableCell>
                      {!isModeB && <TableCell>{t('table.range')}</TableCell>}
                      <TableCell sx={{ minWidth: 200 }}>{t('table.relative')}</TableCell>
                    </TableRow>
                  </TableHead>
                <TableBody>
                  {displayedRows.map((row, rIdx) => (
                    <TableRow
                      key={`${row.storeId}-${row.deviceName || 'dev'}-${row.date}-${row.zoneName || 'geral'}-${rIdx}`}
                      hover
                      sx={{ '&:last-child td': { border: 0 } }}
                    >
                      <TableCell sx={{ pl: 2.5 }}>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {typeof row.date === 'string' && row.date.includes('–') ? row.date : fmtDate(row.date, i18n.language)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                        >{row.storeName}</Typography>
                      </TableCell>
                      {isModeB && (
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                          >{row.deviceName || '—'}</Typography>
                        </TableCell>
                      )}
                      {showZoneColumn && (
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            color="primary.main"
                          >
                            {row.zoneName}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <Typography variant="body2">{row.visitorCount.toLocaleString()}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                        >{fmtDwell(row.dwellSeconds)}</Typography>
                      </TableCell>
                      {!isModeB && (
                        <TableCell>
                          {row.minDwellSeconds != null && row.maxDwellSeconds != null ? (
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              {fmtDwell(row.minDwellSeconds)} – {fmtDwell(row.maxDwellSeconds)}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <LinearProgress
                            key={row.dwellSeconds}
                            variant="determinate"
                            value={(row.dwellSeconds / maxDwell) * 100}
                            sx={{
                              flex: 1,
                              height: 6,
                              borderRadius: 3,
                              bgcolor: 'grey.100',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                                bgcolor: 'primary.main',
                                transition: 'none',
                              },
                            }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ minWidth: 28, textAlign: 'right' }}
                          >
                            {Math.round((row.dwellSeconds / maxDwell) * 100)}%
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {hasFetched && rows.length === 0 && !isLoading && (
                    <EmptyTableRow
                      colSpan={2 + (isModeB ? 1 : 0) + (showZoneColumn ? 1 : 0) + 2 + (isModeB ? 0 : 1) + 1}
                      message={t('table.empty')}
                    />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            </LoadingSkeleton>

            {/* Server-Side TablePagination */}
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
              sx={{ borderTop: '1px solid', borderColor: 'divider', fontSize: 13 }}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default DwellTimePage;
